import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useNavigation } from '@hooks/useNavigation';
import type { AggregatedPosition } from '@hooks/usePositionAggregation';
import type { Order } from '@services/orderTypes';
import type { Asset } from '@shared/types';

export type BottomPanelType = 'trade' | 'closePosition' | 'manageOrders' | null;

export interface TradeData {
  tradeType: 'CALL' | 'PUT';
  strikePrice: number;
  spotPrice: number;
  asset: Asset;
}

export interface ClosePositionData {
  positionGroup: AggregatedPosition;
}

export interface ManageOrdersData {
  orders: Order[];
  strikePrice: number;
  asset: Asset;
}

interface BottomPanelState {
  activePanel: BottomPanelType;
  tradeData: TradeData | null;
  closePositionData: ClosePositionData | null;
  manageOrdersData: ManageOrdersData | null;
}

interface BottomPanelContextValue {
  state: BottomPanelState;
  openTradePanel: (data: TradeData) => void;
  openClosePositionPanel: (data: ClosePositionData) => void;
  openManageOrdersPanel: (data: ManageOrdersData) => void;
  closePanel: () => void;
}

const BottomPanelContext = createContext<BottomPanelContextValue | null>(null);

interface BottomPanelProviderProps {
  children: ReactNode;
}

export function BottomPanelProvider({ children }: BottomPanelProviderProps) {
  const [state, setState] = useState<BottomPanelState>({
    activePanel: null,
    tradeData: null,
    closePositionData: null,
    manageOrdersData: null,
  });

  const { currentPage } = useNavigation();

  const openTradePanel = useCallback((data: TradeData) => {
    setState({
      activePanel: 'trade',
      tradeData: data,
      closePositionData: null,
      manageOrdersData: null,
    });
  }, []);

  const openClosePositionPanel = useCallback((data: ClosePositionData) => {
    setState({
      activePanel: 'closePosition',
      tradeData: null,
      closePositionData: data,
      manageOrdersData: null,
    });
  }, []);

  const openManageOrdersPanel = useCallback((data: ManageOrdersData) => {
    setState({
      activePanel: 'manageOrders',
      tradeData: null,
      closePositionData: null,
      manageOrdersData: data,
    });
  }, []);

  const closePanel = useCallback(() => {
    setState({
      activePanel: null,
      tradeData: null,
      closePositionData: null,
      manageOrdersData: null,
    });
  }, []);

  // Close panels when navigating to a different page
  useEffect(() => {
    closePanel();
  }, [currentPage, closePanel]);

  return (
    <BottomPanelContext.Provider
      value={{
        state,
        openTradePanel,
        openClosePositionPanel,
        openManageOrdersPanel,
        closePanel,
      }}
    >
      {children}
    </BottomPanelContext.Provider>
  );
}

export function useBottomPanel() {
  const context = useContext(BottomPanelContext);
  if (!context) {
    throw new Error('useBottomPanel must be used within a BottomPanelProvider');
  }
  return context;
}
